import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { AlertService } from './alert.service';
import { SageSyncStatus } from 'src/interfaces/sage-sync-status';

/** Backend API response shape for Sage endpoints */
export interface SageApiResponse<T = unknown> {
  success: boolean;
  message?: string;
  data?: T;
  errors?: Record<string, string[]>;
}

/** GET /sage/auth/url — UK OAuth returns redirect URL; Basic (SA) returns url: null, authMode: 'basic'. */
export interface SageAuthUrlData {
  url: string | null;
  authMode?: string;
}

/** Response shape for POST /sage/sync/invoices (batch). Max 30 order_ids per request. */
export interface SageBatchSyncResult {
  synced: number;
  already_synced: number;
  failed: number;
  results?: {
    synced?: number[];
    already_synced?: number[];
    failed?: number[];
  };
}

@Injectable({
  providedIn: 'root',
})
export class SageService {
  private readonly baseUrl = environment.api.baseUrl;
  /** No interceptors — GET /sage/auth/url must be called without Cognito headers (Basic SA spec). */
  private readonly httpPublic: HttpClient;

  constructor(
    httpBackend: HttpBackend,
    private http: HttpClient,
    private _alertService: AlertService
  ) {
    this.httpPublic = new HttpClient(httpBackend);
  }

  /**
   * GET /sage/health - no auth. For connectivity check.
   */
  async getHealth(): Promise<SageApiResponse | null> {
    try {
      const res = await this.http
        .get<SageApiResponse>(`${this.baseUrl}/sage/health`)
        .toPromise();
      return res ?? null;
    } catch (err: any) {
      this.handleError(err, 'Sage health check failed');
      return null;
    }
  }

  /**
   * GET /sage/tax-codes - Cognito auth. 200 = connected, 401 = not connected.
   */
  async getTaxCodes(): Promise<SageApiResponse | null> {
    try {
      const res = await this.http
        .get<SageApiResponse>(`${this.baseUrl}/sage/tax-codes`)
        .toPromise();
      return res ?? null;
    } catch (err: any) {
      if (err?.status === 401) {
        return null;
      }
      this.handleError(err, 'Failed to load Sage tax codes');
      return null;
    }
  }

  /**
   * POST /sage/sync/invoice — backend applies tax/ledger from Sage (SSM); frontend sends order_id only.
   */
  async syncOrderToSage(orderId: number): Promise<boolean> {
    try {
      const res = await this.http
        .post<SageApiResponse>(`${this.baseUrl}/sage/sync/invoice`, {
          order_id: orderId,
        })
        .toPromise();
      if (res?.success && res?.message) {
        this._alertService.infoAlert(res.message);
        return true;
      }
      if (res?.message) {
        this._alertService.warningAlert(res.message);
      }
      return false;
    } catch (err: any) {
      this.handleError(
        err,
        'Sync to Sage failed; you can retry from the order.'
      );
      return false;
    }
  }

  /**
   * POST /sage/sync/invoices with { order_ids } (batch). Max 30 per request. Cognito auth.
   * Use when syncing multiple orders; backend throttles Sage calls. Returns synced / already_synced / failed counts.
   * 207 = partial success; check data.results.failed for failed order ids.
   */
  async syncOrdersToSage(
    orderIds: number[]
  ): Promise<SageApiResponse<SageBatchSyncResult> | null> {
    if (orderIds.length === 0) return null;
    try {
      const res = await this.http
        .post<SageApiResponse<SageBatchSyncResult>>(
          `${this.baseUrl}/sage/sync/invoices`,
          { order_ids: orderIds }
        )
        .toPromise();
      if (res?.success && res?.data) {
        const d = res.data;
        const total = (d.synced ?? 0) + (d.already_synced ?? 0) + (d.failed ?? 0);
        if ((d.failed ?? 0) > 0 && res?.message) {
          this._alertService.warningAlert(
            res.message + (d.results?.failed?.length ? ` Failed order IDs: ${d.results.failed.slice(0, 10).join(', ')}${(d.results.failed?.length ?? 0) > 10 ? '…' : ''}` : '')
          );
        } else if (total > 0 && (d.synced ?? 0) + (d.already_synced ?? 0) > 0) {
          this._alertService.infoAlert(
            res.message ?? `Synced: ${d.synced ?? 0}, already synced: ${d.already_synced ?? 0}${(d.failed ?? 0) > 0 ? `, failed: ${d.failed}` : ''}.`
          );
        }
        return res;
      }
      if (res?.message) this._alertService.warningAlert(res.message);
      return res ?? null;
    } catch (err: any) {
      if (err?.status === 207 && err?.error?.data) {
        const d = err.error.data as SageBatchSyncResult;
        this._alertService.warningAlert(
          `Partial success: synced ${d.synced ?? 0}, already synced ${d.already_synced ?? 0}, failed ${d.failed ?? 0}. ${d.results?.failed?.length ? `Failed order IDs: ${d.results.failed.slice(0, 10).join(', ')}${(d.results.failed?.length ?? 0) > 10 ? '…' : ''}` : ''}`
        );
        return { success: false, data: d };
      }
      this.handleError(err, 'Batch sync to Sage failed. You can retry.');
      return null;
    }
  }

  /**
   * GET /sage/contacts/:id. Cognito auth. Returns Sage contact by id (Sage’s contact id).
   */
  async getContact(sageContactId: string): Promise<SageApiResponse | null> {
    try {
      const res = await this.http
        .get<SageApiResponse>(`${this.baseUrl}/sage/contacts/${encodeURIComponent(sageContactId)}`)
        .toPromise();
      return res?.success ? res : null;
    } catch (err: any) {
      if (err?.status === 404) return null;
      this.handleError(err, 'Failed to load Sage contact');
      return null;
    }
  }

  /**
   * POST /sage/contacts for an individual (person contact). Sends individual_id + display name and contact fields.
   * Backend must accept individual_id, create/link the Sage contact, and persist sage_contact_id on the individual.
   */
  async createContactForIndividual(individual: {
    id: number;
    title?: string;
    first_name: string;
    last_name: string;
    email: string;
    phone_number?: string;
    phone_number_alt?: string;
  }): Promise<{ success: boolean; sage_contact_id?: string } | null> {
    if (!individual?.id) return null;
    const name = [individual.title, individual.first_name, individual.last_name]
      .filter((p) => p != null && String(p).trim() !== '')
      .join(' ')
      .trim();
    try {
      const body = {
        individual_id: individual.id,
        id: individual.id,
        name: name || `${individual.first_name} ${individual.last_name}`.trim(),
        email: individual.email,
        phone_number: individual.phone_number ?? '',
        phone_number_alt: individual.phone_number_alt ?? '',
        vat_registration_number: '',
      };
      const res = await this.http
        .post<SageApiResponse<Record<string, unknown>>>(
          `${this.baseUrl}/sage/contacts`,
          body
        )
        .toPromise();
      if (res?.success && res?.data) {
        const id = this.extractSageContactIdFromPayload(res.data);
        if (id) {
          this._alertService.infoAlert(res.message ?? 'Individual added to Sage.');
          return { success: true, sage_contact_id: id };
        }
        this._alertService.infoAlert(res.message ?? 'Contact created.');
        return { success: true };
      }
      if (res?.message) this._alertService.warningAlert(res.message);
      return res?.success ? { success: true } : null;
    } catch (err: any) {
      this.handleError(err, 'Failed to add individual to Sage.');
      return null;
    }
  }

  /**
   * POST /sage/contacts. Create Sage contact for a company. Body: company_id + company fields (backend maps to Sage contact).
   * Backend returns created contact data; if backend patches company.sage_contact_id, refresh company after.
   */
  async createContactForCompany(company: {
    id?: number;
    name: string;
    email: string;
    phone_number?: string;
    phone_number_alt?: string;
    vat_registration_number?: string;
  }): Promise<{ success: boolean; sage_contact_id?: string } | null> {
    if (!company?.id) return null;
    try {
      const body = {
        company_id: company.id,
        id: company.id,
        name: company.name,
        email: company.email,
        phone_number: company.phone_number ?? '',
        phone_number_alt: company.phone_number_alt ?? '',
        vat_registration_number: company.vat_registration_number ?? '',
      };
      const res = await this.http
        .post<SageApiResponse<Record<string, unknown>>>(
          `${this.baseUrl}/sage/contacts`,
          body
        )
        .toPromise();
      if (res?.success && res?.data) {
        const id = this.extractSageContactIdFromPayload(res.data);
        if (id) {
          this._alertService.infoAlert(res.message ?? 'Company added to Sage.');
          return { success: true, sage_contact_id: id };
        }
        this._alertService.infoAlert(res.message ?? 'Contact created.');
        return { success: true };
      }
      if (res?.message) this._alertService.warningAlert(res.message);
      return res?.success ? { success: true } : null;
    } catch (err: any) {
      this.handleError(err, 'Failed to add company to Sage.');
      return null;
    }
  }

  /**
   * GET /sage/sync/status/:order_id — optional; list/detail orders usually include sage_sync_status.
   */
  async getSyncStatus(orderId: number): Promise<SageSyncStatus | null> {
    try {
      const res = await this.http
        .get<SageApiResponse<SageSyncStatus>>(
          `${this.baseUrl}/sage/sync/status/${orderId}`
        )
        .toPromise();
      if (res?.success && res?.data) {
        return res.data;
      }
      return null;
    } catch (err: any) {
      if (err?.status === 404) {
        return null;
      }
      return null;
    }
  }

  /**
   * GET /sage/auth/url — Basic returns url null; OAuth returns redirect URL. Never redirect when url is null.
   * @param silent If true, no alert on HTTP errors (integrations status check).
   */
  async getSageAuthUrlInfo(
    state?: string,
    silent = false
  ): Promise<SageApiResponse<SageAuthUrlData> | null> {
    try {
      const params = state ? `?state=${encodeURIComponent(state)}` : '';
      // Spec: no IdToken / Bearer on this route (confirms Basic vs OAuth).
      const res = await this.httpPublic
        .get<SageApiResponse<SageAuthUrlData>>(
          `${this.baseUrl}/sage/auth/url${params}`
        )
        .toPromise();
      return res ?? null;
    } catch (err: any) {
      if (!silent) {
        this.handleError(err, 'Failed to get Sage connection status');
      }
      return null;
    }
  }

  /** OAuth (UK) only: redirect when non-empty; Basic (SA) returns null. */
  async getOAuthRedirectUrl(state?: string): Promise<string | null> {
    const res = await this.getSageAuthUrlInfo(state, false);
    const url = res?.data?.url;
    return typeof url === 'string' && url.trim().length > 0 ? url.trim() : null;
  }

  /** Support multiple backend shapes for POST /sage/contacts response data. */
  private extractSageContactIdFromPayload(data: unknown): string | undefined {
    if (data == null) return undefined;
    if (typeof data === 'string' || typeof data === 'number') {
      const s = String(data).trim();
      return s || undefined;
    }
    if (typeof data !== 'object') return undefined;
    const d = data as Record<string, unknown>;
    const contact = d.contact;
    const nested =
      contact && typeof contact === 'object'
        ? (contact as Record<string, unknown>)
        : null;
    const candidates: unknown[] = [
      d.sage_contact_id,
      d.sageContactId,
      d.contact_id,
      d.contactId,
      d.id,
      d.ContactID,
      d.uuid,
      nested?.id,
      nested?.ContactID,
      nested?.uuid,
    ];
    for (const v of candidates) {
      if (v != null && String(v).trim() !== '') return String(v);
    }
    return undefined;
  }

  private handleError(err: any, fallbackMessage: string): void {
    const status = err?.status;
    const msg =
      err?.error?.message || err?.message || fallbackMessage;
    if (status === 401) {
      this._alertService.warningAlert(
        'Sage: session expired or not signed in — sign in again. (401 is Cognito, not Sage OAuth.)'
      );
    } else if (status === 404) {
      this._alertService.warningAlert(msg || 'Resource not found.');
    } else if (status >= 500) {
      this._alertService.errorAlert(
        msg || 'Server error. Please try again later.'
      );
    } else {
      this._alertService.errorAlert(msg);
    }
  }
}
